import type {AuthUser} from '../auth/types'

type Viewer = Pick<AuthUser, 'id' | 'role'> &
    Partial<Pick<AuthUser, 'isSalesRep' | 'dispatchYardId'>>

type WorkOwners = {
    repId?: string | null
    projectCreatedById?: string | null
    createdById?: string | null
}

export function isManager(user: Viewer): boolean {
    return user.role === 'admin' || user.role === 'manager'
}

/** Owner is the rep, else the project creator, else the job creator. */
export function ownsWork(user: Viewer | null | undefined, owners: WorkOwners): boolean {
    if (!user || !user.isSalesRep || isManager(user)) {
        return true
    }

    const owner = [owners.repId, owners.projectCreatedById, owners.createdById].find(
        (id): id is string => Boolean(id),
    )
    return owner === undefined || owner === user.id
}

/** True for the sales rep who owns the work: they may change it in any yard. */
export function ownsAsRep(user: Viewer | null | undefined, owners: WorkOwners): boolean {
    if (!user || !user.isSalesRep || isManager(user)) {
        return false
    }

    const owner = [owners.repId, owners.projectCreatedById, owners.createdById].find(
        (id): id is string => Boolean(id),
    )
    return owner === user.id
}

/** True when either yard is unset, the yards match, or the user is an admin. */
export function inUsersYard(
    user: Viewer | null | undefined,
    workYardId: string | null | undefined,
): boolean {
    return (
        !user ||
        user.role === 'admin' ||
        user.dispatchYardId == null ||
        workYardId == null ||
        workYardId === user.dispatchYardId
    )
}

export function canEditSales(
    user: Viewer | null | undefined,
    owners: WorkOwners,
    workYardId?: string | null,
): boolean {
    return ownsWork(user, owners) && (inUsersYard(user, workYardId) || ownsAsRep(user, owners))
}

/** True when the user may dispatch the work: the yard rule, waived for the owning rep. */
export function canDispatchWork(
    user: Viewer | null | undefined,
    owners: WorkOwners,
    workYardId: string | null | undefined,
): boolean {
    return ownsWork(user, owners) && (!isOtherYardJob(user, workYardId) || ownsAsRep(user, owners))
}

type WorkKind = 'job' | 'order' | 'quote'

export function viewOnlyNote(repName: string | null | undefined, what: WorkKind): string {
    const owner = repName ? `${repName}'s` : "another rep's"
    return `This is ${owner} ${what}. View only.`
}

export function editBlockedNote({
    user,
    owners,
    repName,
    workYardId,
    workYardName,
    what,
}: {
    user: Viewer | null | undefined
    owners: WorkOwners
    repName: string | null | undefined
    workYardId?: string | null
    workYardName?: string | null
    what: WorkKind
}): string | null {
    if (!ownsWork(user, owners)) {
        return viewOnlyNote(repName, what)
    }
    if (!inUsersYard(user, workYardId) && !ownsAsRep(user, owners)) {
        const owner = workYardName ? `${workYardName}'s` : "the other yard's"
        return `This is ${owner} ${what}. View only.`
    }
    return null
}

/** True when the job's yard differs from the user's yard. Always false for admins. */
export function isOtherYardJob(
    user: Viewer | null | undefined,
    jobYardId: string | null | undefined,
): boolean {
    return (
        user != null &&
        user.role !== 'admin' &&
        user.dispatchYardId != null &&
        jobYardId != null &&
        jobYardId !== user.dispatchYardId
    )
}

/** Admins only: a move corrects a rep's wrong yard. A short yard borrows drivers instead. */
export function canMoveBetweenYards(user: Viewer | null | undefined): boolean {
    return user?.role === 'admin'
}

export function canChangeYardItem(user: Viewer, itemYardId: string | null | undefined): boolean {
    if (user.isSalesRep && !isManager(user)) {
        return false
    }

    return inUsersYard(user, itemYardId)
}

/** True for the yard's dispatcher, managers of the yard and admins. A job with no yard counts as any yard's. */
export function canPublish(
    user: Viewer | null | undefined,
    yardId: string | null | undefined,
): boolean {
    if (user == null) {
        return false
    }
    if (isManager(user)) {
        return inUsersYard(user, yardId)
    }
    if (user.isSalesRep || user.dispatchYardId == null) {
        return false
    }

    return yardId == null || yardId === user.dispatchYardId
}

export function publishableYards<Yard extends {id: string}>(
    user: Viewer | null | undefined,
    yards: Yard[],
): Yard[] {
    return yards.filter((yard) => canPublish(user, yard.id))
}
